// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'

// ── Class name utility ────────────────────────────────────────────────────────
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ── UUID generation ───────────────────────────────────────────────────────────
export function generateUUID(): string {
  return crypto.randomUUID()
}

// ── Date utilities ────────────────────────────────────────────────────────────
export function formatDateNL(dateString: string): string {
  try {
    return format(parseISO(dateString), 'd MMMM yyyy', { locale: nl })
  } catch {
    return dateString
  }
}

export function formatDateTimeNL(dateString: string): string {
  try {
    return format(parseISO(dateString), "d MMMM yyyy 'om' HH:mm", { locale: nl })
  } catch {
    return dateString
  }
}

export function nowISO(): string {
  return new Date().toISOString()
}

export function addYearsISO(years: number): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() + years)
  return d.toISOString()
}

export function addDaysISO(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString()
}

// ── Text utilities ────────────────────────────────────────────────────────────
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength) + '…'
}

export function capitalize(str: string): string {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// ── Validation ────────────────────────────────────────────────────────────────
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function isValidDate(dateStr: string): boolean {
  const d = new Date(dateStr)
  return !isNaN(d.getTime())
}

export function isValidDutchPostalCode(code: string): boolean {
  return /^[1-9][0-9]{3}\s?[A-Z]{2}$/.test(code.toUpperCase())
}

// ── Progress calculations ─────────────────────────────────────────────────────
export function calculateProgress(current: number, total: number): number {
  if (total === 0) return 0
  return Math.round((current / total) * 100)
}

// ── Relationship label mapping ────────────────────────────────────────────────
export const RELATIONSHIP_LABELS: Record<string, string> = {
  PARTNER: 'Partner / Echtgenoot·e',
  CHILD:   'Kind',
  SIBLING: 'Broer of zus',
  FRIEND:  'Vriend of vriendin',
  OTHER:   'Overig',
}

// ── Marital status labels ─────────────────────────────────────────────────────
export const MARITAL_STATUS_LABELS: Record<string, string> = {
  single:      'Ongehuwd',
  married:     'Getrouwd',
  registered:  'Geregistreerd partnerschap',
  divorced:    'Gescheiden',
  widowed:     'Weduwe / Weduwnaar',
}

// ── Password strength ─────────────────────────────────────────────────────────
export function checkPasswordStrength(password: string): {
  score:  0 | 1 | 2 | 3 | 4
  label:  string
  color:  string
} {
  let score = 0
  if (password.length >= 8)  score++
  if (password.length >= 12) score++
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++
  if (/[0-9]/.test(password)) score++
  if (/[^A-Za-z0-9]/.test(password)) score++

  const capped = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4

  const labels = ['Te zwak', 'Zwak', 'Redelijk', 'Goed', 'Sterk']
  const colors = [
    'bg-red-400',
    'bg-orange-400',
    'bg-yellow-400',
    'bg-sage-400',
    'bg-sage-600',
  ]

  return {
    score: capped,
    label: labels[capped],
    color: colors[capped],
  }
}
